import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Play, ArrowRight } from "lucide-react";
import { HERO_SLIDES } from "@/data/content";
import { resolveAssetPath } from "@/lib/assets";

export const HeroBanner = () => {
  const [idx, setIdx] = useState(0);
  const count = HERO_SLIDES.length;
  const slide = HERO_SLIDES[idx];

  // Auto rotate (longer dwell on video)
  useEffect(() => {
    const isVideo = slide.type === "video";
    const t = setTimeout(() => setIdx((i) => (i + 1) % count), isVideo ? 10000 : 6000);
    return () => clearTimeout(t);
  }, [idx, slide.type, count]);

  const prev = () => setIdx((i) => (i - 1 + count) % count);
  const next = () => setIdx((i) => (i + 1) % count);

  return (
    <section data-testid="hero-banner" className="bg-[var(--soft)] py-6">
      <div className="max-w-[1280px] mx-auto px-4 lg:px-6">
        <div className="relative rounded-xl overflow-hidden bg-[var(--navy)] text-white shadow-lg h-[440px] md:h-[480px]">
          {/* Background layer */}
          {slide.type === "video" ? (
            <div className="absolute inset-0 overflow-hidden bg-black">
              {/* Poster fallback (always shown until video plays) */}
              <img
                src={resolveAssetPath(slide.poster)}
                alt=""
                className="absolute inset-0 w-full h-full object-cover"
                aria-hidden
              />
              <video
                key={slide.videoSources?.[0]}
                data-testid="hero-video"
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                poster={resolveAssetPath(slide.poster)}
                className="absolute inset-0 w-full h-full object-cover"
              >
                {slide.videoSources?.map((src) => (
                  <source key={src} src={resolveAssetPath(src)} type="video/mp4" />
                ))}
              </video>
            </div>
          ) : (
            <img
              src={resolveAssetPath(slide.image)}
              alt={slide.headline}
              data-testid={`hero-slide-image-${idx}`}
              className="absolute inset-0 w-full h-full object-cover"
            />
          )}
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#0b2545]/95 via-[#0b2545]/70 to-transparent pointer-events-none" />

          {/* Content */}
          <div
            key={idx}
            className="relative h-full flex flex-col justify-center p-6 md:p-12 max-w-3xl fade-up"
          >
            {slide.eyebrow && (
              <div className="text-[11px] md:text-xs uppercase tracking-[0.2em] text-white/80 mb-3">
                {slide.eyebrow}
              </div>
            )}
            <h1 className="font-serif-display text-3xl md:text-5xl lg:text-6xl font-bold leading-[1.05]">
              {slide.headline}{" "}
              <span className="text-[var(--yellow)]">
                {slide.headlineAccent}
              </span>
            </h1>
            {slide.description && (
              <p className="mt-4 text-sm md:text-base text-white/85 max-w-xl">
                {slide.description}
              </p>
            )}
            {slide.price && (
              <div className="mt-4 flex items-baseline flex-wrap gap-2">
                <span className="font-serif-display text-4xl md:text-5xl font-bold text-white">
                  {slide.price}
                </span>
                <span className="text-white/75 text-sm">
                  {slide.priceSuffix}
                </span>
              </div>
            )}
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <button
                type="button"
                data-testid={`hero-cta-${idx}`}
                onClick={() =>
                  document
                    .getElementById("packages")
                    ?.scrollIntoView({ behavior: "smooth" })
                }
                className="bg-[var(--yellow)] hover:bg-[var(--yellow-2)] text-[var(--navy)] hover:text-white font-semibold text-sm px-7 py-3 rounded transition-colors inline-flex items-center gap-2"
              >
                {slide.ctaLabel}
                <ArrowRight className="w-4 h-4" />
              </button>
              {slide.type === "video" && (
                <div className="inline-flex items-center gap-2 text-xs text-white/70">
                  <span className="w-7 h-7 rounded-full border border-white/30 flex items-center justify-center">
                    <Play className="w-3 h-3" />
                  </span>
                  Now playing
                </div>
              )}
            </div>
          </div>

          {/* Prev / Next */}
          <button
            type="button"
            onClick={prev}
            data-testid="hero-prev"
            aria-label="previous slide"
            className="absolute left-3 md:left-4 bottom-2 md:bottom-auto md:top-1/2 md:-translate-y-1/2 w-10 h-10 rounded-full bg-white/15 hover:bg-white text-white hover:text-[var(--navy)] flex items-center justify-center backdrop-blur transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={next}
            data-testid="hero-next"
            aria-label="next slide"
            className="absolute right-3 md:right-4 bottom-2 md:bottom-auto md:top-1/2 md:-translate-y-1/2 w-10 h-10 rounded-full bg-white/15 hover:bg-white text-white hover:text-[var(--navy)] flex items-center justify-center backdrop-blur transition-colors"
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          {/* Dots */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2">
            {HERO_SLIDES.map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`go to slide ${i + 1}`}
                data-testid={`hero-dot-${i}`}
                onClick={() => setIdx(i)}
                className={`h-1.5 rounded-full transition-all ${
                  i === idx
                    ? "w-8 bg-[var(--yellow)]"
                    : "w-3 bg-white/50 hover:bg-white/70"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
